// Una squadra non puo' giocare due partite nello stesso turno.
//
// Il punto delicato e' dire chi e' la squadra, perche' nel planning una casella puo' contenere tre
// cose diverse, e solo la prima e' una squadra per davvero:
//
//   - una squadra iscritta e risolta, che ha un id: allora l'identita' e' l'id, non il nome. Due
//     iscrizioni possono chiamarsi uguale, e la stessa squadra puo' comparire con nomi scritti in
//     modo diverso arrivando da rami diversi della formula.
//   - un posto in una classifica ancora da fare ("Girone 10 #4", "Classifica Terze sabato #2").
//     Il nodo di provenienza e' unico nella formula - il calcolo delle classifiche lo indicizza per
//     tipo e nome, quindi due nodi omonimi si pesterebbero i piedi - percio' la stringa intera
//     identifica gia' il posto, e due posti uguali nello stesso turno sono la stessa squadra futura.
//   - il vincitore o il perdente di una partita a eliminazione diretta ("Win:Ottavo 1"). Qui il
//     nome NON basta: il riferimento e' relativo al tabellone che lo ospita, esattamente come lo
//     risolve il backend, che cerca "<nodo>-<partita sorgente>". Sul torneo 98 "Win:Ottavo 1" vale
//     due volte, una in "Dirette 1-Quarto 1" e una in "Dirette 2-Quarto 1", e sono due squadre
//     diverse: confrontare le stringhe segnalava un doppio impegno che non esiste.
//
// La fase non entra nella chiave, ed e' voluto: due partite dello stesso turno in fasi diverse che
// pescano dallo stesso posto pescano la stessa squadra, e vanno segnalate. A distinguere e' il
// nodo di provenienza, che per le dirette e' il tabellone e per i posti e' il nome del nodo.

/** "Win:Ottavo 1" / "Los:Semifinale 2": prefisso e partita sorgente. */
const SEGNAPOSTO_DIRETTA = /^(win|los):(.+)$/i;

// Il ripiego del backend quando non riesce a risolvere la provenienza. Due "Sconosciuto" non sono
// la stessa squadra: sono due buchi.
const NOMI_SENZA_SQUADRA = new Set(['riposo', 'sconosciuto']);

/**
 * Il tabellone che ospita la partita. Il nome di una diretta e' "<nodo>-<partita>", per esempio
 * "Dirette 2-Quarto 1"; senza trattino il nome e' gia' il nodo.
 */
const nodoDirette = (match) => {
  const nome = (match.nome ?? '').trim();
  const taglio = nome.indexOf('-');
  return (taglio > 0 ? nome.slice(0, taglio) : nome).trim();
};

/**
 * Chi occupa una casella: una chiave con cui confrontarla con le altre, e l'etichetta da mostrare.
 * Torna null quando la casella non identifica nessuna squadra.
 */
const identita = (match, nome, id) => {
  const pulito = (nome ?? '').trim();

  // La squadra e' nota: vale l'id, il nome e' solo l'etichetta.
  if (id > 0) return { chiave: `id:${id}`, etichetta: pulito || `squadra ${id}` };

  if (!pulito || NOMI_SENZA_SQUADRA.has(pulito.toLowerCase())) return null;

  const diretta = SEGNAPOSTO_DIRETTA.exec(pulito);
  if (diretta) {
    const nodo = nodoDirette(match);
    return {
      chiave: `${diretta[1].toLowerCase()}:${nodo.toLowerCase()}-${diretta[2].trim().toLowerCase()}`,
      etichetta: nodo ? `${pulito} (${nodo})` : pulito,
    };
  }

  return { chiave: `posto:${pulito.toLowerCase()}`, etichetta: pulito };
};

/** Le due caselle di una partita, per chiave. Una squadra contro se stessa conta una volta sola. */
const occupantiDellaPartita = (match) => {
  const occupanti = new Map();
  [[match.squadra1, match.s1Id], [match.squadra2, match.s2Id]].forEach(([nome, id]) => {
    const chi = identita(match, nome, id);
    if (chi && !occupanti.has(chi.chiave)) occupanti.set(chi.chiave, chi);
  });
  return occupanti;
};

/**
 * Chi gioca piu' di una volta nello stesso turno.
 *
 * @param {Array<object>} matches le partite in calendario (la panchina, turno 0, non c'entra)
 * @returns {Array<{turno: number, squadra: string, partite: object[]}>} ordinati per turno e nome
 */
export const doppiImpegniPerTurno = (matches) => {
  const perTurno = new Map();

  matches.forEach((match) => {
    if (!perTurno.has(match.turno)) perTurno.set(match.turno, new Map());
    const delTurno = perTurno.get(match.turno);

    occupantiDellaPartita(match).forEach(({ chiave, etichetta }) => {
      if (!delTurno.has(chiave)) delTurno.set(chiave, { squadra: etichetta, partite: [] });
      delTurno.get(chiave).partite.push(match);
    });
  });

  const doppi = [];
  perTurno.forEach((delTurno, turno) => {
    delTurno.forEach(({ squadra, partite }) => {
      if (partite.length > 1) doppi.push({ turno, squadra, partite });
    });
  });

  return doppi.sort((a, b) => a.turno - b.turno || a.squadra.localeCompare(b.squadra));
};

/** Le partite da incorniciare di rosso: quelle che compaiono in almeno un doppio impegno. */
export const partiteInDoppioImpegno = (doppi) => new Set(doppi.flatMap((d) => d.partite));
